"use client";

import { AlignCenter, AlignLeft, AlignRight, Palette } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    BUTTON_ALIGNS,
    BUTTON_SIZES,
    BUTTON_STYLES,
    ButtonAppearance,
    ButtonBlock,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import { useLomdaDir } from "../../hooks/useLomdaLocale";
import { buttonColors, resolveButtonAppearance } from "../../lib/blockRegistry";
import { DEFAULT_THEME } from "../../lib/theme";
import {
    AppearanceReset,
    AppearanceRow,
    ColorChipField,
    PopoverTitle,
    SegmentedToggle,
    chromeBtn,
    chromeBtnOpen,
    popoverShell,
} from "./blockAppearance";

type ColorKey = "color" | "textColor";

export function ButtonAppearancePopover({ block }: { block: ButtonBlock }) {
    const patchBlockData = useLomdaV3Editor((s) => s.patchBlockData);
    const theme = useLomdaV3Editor((s) => s.metadata.theme) ?? DEFAULT_THEME;
    const dir = useLomdaDir();
    const appearance = resolveButtonAppearance(block.data.appearance);
    const shown = buttonColors(block.data.appearance, theme);
    const title = t("lomda-v3-editor.button.appearance");

    const update = (patch: ButtonAppearance) =>
        patchBlockData(block.id, "button", (d) => ({
            appearance: { ...d.appearance, ...patch },
        }));
    // Confirming the colour the theme gives must not freeze it as a fixed one.
    const setColor = (key: ColorKey, value: string) =>
        patchBlockData(block.id, "button", (d) =>
            value.toLowerCase() ===
            buttonColors(d.appearance, theme)[key].toLowerCase()
                ? null
                : { appearance: { ...d.appearance, [key]: value } }
        );
    const reset = () =>
        patchBlockData(block.id, "button", (d) =>
            d.appearance === undefined ? null : { appearance: undefined }
        );

    const StartIcon = dir === "rtl" ? AlignRight : AlignLeft;
    const EndIcon = dir === "rtl" ? AlignLeft : AlignRight;
    const alignIcon = { start: StartIcon, center: AlignCenter, end: EndIcon };
    const alignName = {
        start: t("lomda-v3-editor.format.align-start"),
        center: t("lomda-v3-editor.format.align-center"),
        end: t("lomda-v3-editor.format.align-end"),
    };
    const colorLabel = t("lomda-v3-editor.button.color");
    const textColorLabel = t("lomda-v3-editor.button.text-color");

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <Palette className="size-5" />
                    {title}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className={cn(popoverShell, "w-[340px]")}
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>{title}</PopoverTitle>

                    <AppearanceRow label={t("lomda-v3-editor.button.style")}>
                        <SegmentedToggle
                            label={t("lomda-v3-editor.button.style")}
                            value={appearance.style}
                            onChange={(style) => update({ style })}
                            options={BUTTON_STYLES.map((v) => ({
                                value: v,
                                content: t(`lomda-v3-editor.button.style.${v}`),
                            }))}
                        />
                    </AppearanceRow>

                    <AppearanceRow label={colorLabel}>
                        <ColorChipField
                            label={colorLabel}
                            value={shown.color}
                            onChange={(v) => setColor("color", v)}
                        />
                    </AppearanceRow>

                    <AppearanceRow label={textColorLabel}>
                        <ColorChipField
                            label={textColorLabel}
                            value={shown.textColor}
                            onChange={(v) => setColor("textColor", v)}
                        />
                    </AppearanceRow>

                    <AppearanceRow label={t("lomda-v3-editor.button.size")}>
                        <SegmentedToggle
                            label={t("lomda-v3-editor.button.size")}
                            value={appearance.size}
                            onChange={(size) => update({ size })}
                            options={BUTTON_SIZES.map((v) => ({
                                value: v,
                                name: t(
                                    `lomda-v3-editor.button.size.${v}-name`
                                ),
                                content: t(`lomda-v3-editor.button.size.${v}`),
                            }))}
                        />
                    </AppearanceRow>

                    <AppearanceRow label={t("lomda-v3-editor.button.align")}>
                        <SegmentedToggle
                            label={t("lomda-v3-editor.button.align")}
                            value={appearance.align}
                            onChange={(align) => update({ align })}
                            options={BUTTON_ALIGNS.map((v) => {
                                const Icon = alignIcon[v];
                                return {
                                    value: v,
                                    name: alignName[v],
                                    content: <Icon className="size-4" />,
                                };
                            })}
                        />
                    </AppearanceRow>

                    <AppearanceReset onReset={reset} />
                </div>
            </PopoverContent>
        </Popover>
    );
}
